/* eslint-disable no-underscore-dangle */
import { useRouter } from 'next/navigation';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentMethodEnum } from '@/__generated_graphql__/graphql';
import { BodyModal } from '@/components/AdvertisePayments/components/Body/Body';
import { ExtraContentModal } from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent';
import {
  detectDefaultPaymentMethod,
  getCurrentService,
  mapPaymentMethods
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.helpers';
import {
  CardId,
  ServiceTypeMap
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { FooterModal } from '@/components/AdvertisePayments/components/Footer/Footer';
import { HeaderModal } from '@/components/AdvertisePayments/components/Header/Header';
import { HEADER_TYPE_MODALS } from '@/components/AdvertisePayments/components/Header/Header.types';
import { PaymentsModalProps } from '@/components/AdvertisePayments/components/Modal/Modal.types';
import { FREE_WORD_REGEX, PaymentTypeEnum, ROUTES } from '@/constants/common';
import { PAYMENT_MODAL_CY } from '@/constants/test-id';

import { mapTypenameToAdType } from '../../AdvertisePayments.helpers';
import { useContentOptionsConfig } from '../../hooks/useContentOptionsConfig';
import { useFreeActivate } from '../../hooks/useFreeActivate';
import { getFooterVariant } from '../Footer/Footer.helpers';
import { OptionsModal } from '../Options/Options';
import { OptionsContentModal } from '../Options/OptionsContent';
import * as S from './Modal.styles';

export const PaymentsModal: FC<PaymentsModalProps> = ({
  modalController,
  mainModal,
  ad,
  type = PaymentTypeEnum.ADVERTISE,
  contentTitle,
  onConfirmPayment
}) => {
  const router = useRouter();
  const {
    isExtraContentVisible,
    isAddBalance,
    modalOverlayRef,
    shouldRender,
    isVisible,
    activeCard,
    activePaidServiceId,
    activePeriodId,
    isLoading,
    setIsLoading,
    onContinue,
    onPay,
    closeExtraContent,
    handleClickOutside,
    setActiveCard,
    setIsAddBalance,
    setActivePaidServiceId,
    setActivePeriodId
  } = modalController;
  const { PUBLICATION } = PaymentTypeEnum;

  const [selectedPaymentWay, setSelectedPaymentWay] =
    useState<PaymentMethodEnum>(PaymentMethodEnum.Azericard);
  const prevIsAddBalanceRef = useRef(isAddBalance);

  const { t } = useTranslation();

  const contentConfig = useContentOptionsConfig();
  const translationConfig = contentConfig[activeCard as CardId];

  const paidServices = ad?.paidServices;
  const serviceType = ServiceTypeMap[activeCard as CardId];

  const currentService = useMemo(
    () => getCurrentService(paidServices, serviceType),
    [paidServices, serviceType]
  );

  const methods = useMemo(
    () => mapPaymentMethods(currentService?.paymentMethods),
    [currentService?.paymentMethods]
  );

  useEffect(() => {
    if (!methods || methods.length === 0) return;

    const defaultPaymentMethod =
      detectDefaultPaymentMethod(methods) ?? PaymentMethodEnum.Yigim;

    setSelectedPaymentWay(prev =>
      methods.some(method => method.id === prev) ? prev : defaultPaymentMethod
    );
  }, [methods, activeCard]);

  useEffect(() => {
    const wasAddBalance = prevIsAddBalanceRef.current;
    const exitedAddBalance =
      wasAddBalance && !isAddBalance && type === PaymentTypeEnum.ADVERTISE;

    if (exitedAddBalance) {
      setSelectedPaymentWay(PaymentMethodEnum.Wallet);
    }

    prevIsAddBalanceRef.current = isAddBalance;
  }, [isAddBalance, type]);

  const { activate, loading } = useFreeActivate({
    ad,
    succesCallback: () => {
      mainModal.close();
      if (ad.id && ad.__typename)
        router.replace(
          ROUTES.PUBLISH_AD_INFO(ad.id, mapTypenameToAdType(ad.__typename))
        );
    },
    errorCallback: () => {
      mainModal.close();
      modalController.uiModals.error.open();
    }
  });

  if (!shouldRender) return null;

  const isPublication = type === PUBLICATION;
  const isPublicationView = isPublication && !isAddBalance;

  const handlePay = async () => {
    if (!ad?.id) return;
    const isFreePayment = FREE_WORD_REGEX.test(activeCard);

    if (isFreePayment) activate();
    else onPay(ad.id, ad.__typename, activeCard);
  };

  const handleExtraContentClose = () => {
    if (isAddBalance && type === PaymentTypeEnum.ADVERTISE) {
      setIsAddBalance(false);
      setSelectedPaymentWay(PaymentMethodEnum.Wallet);
      return;
    }
    mainModal.close();
  };

  return (
    <>
      <S.ModalOverlay
        $isOpen={isVisible}
        ref={modalOverlayRef}
        onClick={handleClickOutside}
        data-cy={PAYMENT_MODAL_CY.MODAL_OVERLAY}
      />
      <S.ModalContainer
        data-cy={PAYMENT_MODAL_CY.MAIN_MODAL}
        $isOpen={isVisible}
        $isPublication={isPublicationView}
        $isExtraContentVisible={isExtraContentVisible}
      >
        {isExtraContentVisible ? (
          <ExtraContentModal
            isExtraContentVisible={isExtraContentVisible}
            isAddBalance={isAddBalance}
            activeCard={activeCard}
            ad={ad}
            type={type}
            activePaidServiceId={activePaidServiceId}
            activePeriodId={activePeriodId}
            contentTitle={contentTitle}
            isLoading={isLoading}
            paidServices={paidServices}
            setIsLoading={setIsLoading}
            onBack={closeExtraContent}
            onClose={handleExtraContentClose}
            onConfirmPayment={onConfirmPayment}
            setActivePaidServiceId={setActivePaidServiceId}
            setActivePeriodId={setActivePeriodId}
            selectedPaymentWay={selectedPaymentWay}
            onChangePaymentWay={setSelectedPaymentWay}
          />
        ) : (
          <>
            <HeaderModal
              type={HEADER_TYPE_MODALS.MAIN}
              contentTitle={contentTitle}
              onModalClose={mainModal.close}
            />
            <BodyModal>
              <OptionsModal
                items={paidServices?.[0]?.options ?? []}
                type={type}
                activeCard={activeCard}
                setActiveCard={setActiveCard}
              />

              {!isPublication && translationConfig && (
                <OptionsContentModal
                  contentOptions={translationConfig.options(t)}
                />
              )}
            </BodyModal>
            <FooterModal
              isLoading={loading}
              footerVariant={getFooterVariant(type)}
              onContinue={onContinue}
              onCancel={mainModal.close}
              onPay={handlePay}
            />
          </>
        )}
      </S.ModalContainer>
    </>
  );
};
